/* SECOND SPRING — cart drawer.
   IIFE, no libraries. Inits on DOMContentLoaded and shopify:section:load,
   double-init guarded with a dataset flag.

   Opens on any [data-ss-cart-open] click and on the "ss:cart:add" CustomEvent
   (dispatched on document by the PDP hero after a successful add, which also
   triggers a refresh). Cart state always comes from window.SecondSpring.routes.cart. */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function routes() {
    return (window.SecondSpring && window.SecondSpring.routes) || {
      cart: '/cart.js',
      cart_change: '/cart/change.js',
      cart_add: '/cart/add.js'
    };
  }

  /* ---- money ---- */

  function formatNumber(value, precision, thousands, decimal) {
    var fixed = value.toFixed(precision);
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  }

  function formatMoney(cents) {
    var format = (window.SecondSpring && window.SecondSpring.moneyFormat) || '${{amount}}';
    var value = cents / 100;
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      switch (key) {
        case 'amount': return formatNumber(value, 2, ',', '.');
        case 'amount_no_decimals': return formatNumber(Math.round(value), 0, ',', '.');
        case 'amount_with_comma_separator': return formatNumber(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return formatNumber(Math.round(value), 0, '.', ',');
        case 'amount_with_apostrophe_separator': return formatNumber(value, 2, "'", '.');
        default: return formatNumber(value, 2, ',', '.');
      }
    });
  }

  /* ---- DOM helpers ---- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function imageUrl(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* ---- drawer ---- */

  function initDrawer(root) {
    if (root.dataset.ssInit === 'true') return;
    root.dataset.ssInit = 'true';

    var panel = root.querySelector('.ss-cart-drawer__panel');
    var linesEl = root.querySelector('[data-ss-cart-lines]');
    var emptyEl = root.querySelector('[data-ss-cart-empty]');
    var footEl = root.querySelector('[data-ss-cart-foot]');
    var subtotalEl = root.querySelector('[data-ss-cart-subtotal]');
    var shipbar = root.querySelector('[data-ss-shipbar]');
    var shipbarText = root.querySelector('[data-ss-shipbar-text]');
    var shipbarTrack = root.querySelector('[data-ss-shipbar-track]');
    var shipbarFill = root.querySelector('[data-ss-shipbar-fill]');

    var thresholdCents = parseInt(root.dataset.thresholdCents, 10) || 0;
    var progressText = root.dataset.progressText || '';
    var unlockedText = root.dataset.unlockedText || '';

    var lastFocused = null;
    var pending = false;

    /* -- render -- */

    function renderLine(item, index) {
      var li = el('li', 'ss-cart-drawer__line');
      li.dataset.key = item.key;

      var media = el('div', 'ss-cart-drawer__line-media');
      if (item.image) {
        var img = document.createElement('img');
        img.src = imageUrl(item.image, 128);
        img.alt = '';
        img.width = 64;
        img.height = 64;
        img.loading = 'lazy';
        media.appendChild(img);
      }
      li.appendChild(media);

      var info = el('div', 'ss-cart-drawer__line-info');

      var top = el('div', 'ss-cart-drawer__line-top');
      var titleWrap = el('div');
      titleWrap.appendChild(el('p', 'ss-cart-drawer__line-title', item.product_title));

      var metaBits = [];
      if (item.variant_title && item.variant_title !== 'Default Title') metaBits.push(item.variant_title);
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        metaBits.push(item.selling_plan_allocation.selling_plan.name);
      }
      if (metaBits.length) titleWrap.appendChild(el('p', 'ss-cart-drawer__line-meta', metaBits.join(' · ')));

      top.appendChild(titleWrap);
      top.appendChild(el('span', 'ss-cart-drawer__line-price', formatMoney(item.final_line_price)));
      info.appendChild(top);

      var controls = el('div', 'ss-cart-drawer__line-controls');

      var qty = el('div', 'ss-cart-drawer__qty');
      var dec = el('button', 'ss-cart-drawer__qty-btn');
      dec.type = 'button';
      dec.setAttribute('aria-label', 'Decrease quantity of ' + item.product_title);
      dec.dataset.qtyChange = String(item.quantity - 1);
      dec.textContent = '−';
      var val = el('span', 'ss-cart-drawer__qty-value', String(item.quantity));
      val.setAttribute('aria-label', 'Quantity: ' + item.quantity);
      var inc = el('button', 'ss-cart-drawer__qty-btn');
      inc.type = 'button';
      inc.setAttribute('aria-label', 'Increase quantity of ' + item.product_title);
      inc.dataset.qtyChange = String(item.quantity + 1);
      inc.textContent = '+';
      qty.appendChild(dec);
      qty.appendChild(val);
      qty.appendChild(inc);
      controls.appendChild(qty);

      var remove = el('button', 'ss-cart-drawer__remove', 'Remove');
      remove.type = 'button';
      remove.dataset.qtyChange = '0';
      remove.setAttribute('aria-label', 'Remove ' + item.product_title + ' from cart');
      controls.appendChild(remove);

      info.appendChild(controls);
      li.appendChild(info);
      return li;
    }

    function renderShipbar(cart) {
      if (!shipbar || thresholdCents <= 0) return;
      if (cart.item_count === 0) {
        shipbar.hidden = true;
        return;
      }
      shipbar.hidden = false;
      var subtotal = cart.items_subtotal_price;
      var remaining = thresholdCents - subtotal;
      var percent = Math.max(0, Math.min(100, Math.round((subtotal / thresholdCents) * 100)));

      if (remaining <= 0) {
        shipbar.classList.add('is-unlocked');
        shipbarText.textContent = unlockedText;
      } else {
        shipbar.classList.remove('is-unlocked');
        shipbarText.textContent = '';
        var pieces = progressText.split('[amount]');
        shipbarText.appendChild(document.createTextNode(pieces[0] || ''));
        if (pieces.length > 1) {
          shipbarText.appendChild(el('strong', null, formatMoney(remaining)));
          shipbarText.appendChild(document.createTextNode(pieces.slice(1).join('[amount]')));
        }
      }
      shipbarFill.style.width = percent + '%';
      shipbarTrack.setAttribute('aria-valuenow', String(percent));
    }

    function render(cart) {
      linesEl.textContent = '';
      var isEmpty = cart.item_count === 0;

      if (!isEmpty) {
        for (var i = 0; i < cart.items.length; i++) {
          linesEl.appendChild(renderLine(cart.items[i], i));
        }
        /* Show the DISCOUNTED total. items_subtotal_price is pre-discount, which made a
       5-bottle cart read $345 while the customer actually pays $207, hiding the free
       bottles until checkout. Show the real number plus the saving. */
    var saved = cart.items_subtotal_price - cart.total_price;
    subtotalEl.textContent = formatMoney(cart.total_price);
    var savingsEl = root.querySelector('[data-ss-cart-savings]');
    if (saved > 0) {
      if (!savingsEl) {
        savingsEl = document.createElement('div');
        savingsEl.setAttribute('data-ss-cart-savings', '');
        savingsEl.style.cssText = 'display:flex;justify-content:space-between;font:600 13px/1.4 var(--ss-ui);color:var(--ss-bloom-text,#B0492F);margin-bottom:6px;';
        var row = subtotalEl.parentElement;
        row.parentElement.insertBefore(savingsEl, row);
      }
      savingsEl.innerHTML = '<span>Bundle savings</span><span>-' + formatMoney(saved) + '</span>';
      savingsEl.style.display = 'flex';
    } else if (savingsEl) {
      savingsEl.style.display = 'none';
    }
      }

      emptyEl.hidden = !isEmpty;
      footEl.hidden = isEmpty;
      renderShipbar(cart);

      document.dispatchEvent(new CustomEvent('ss:cart:updated', {
        detail: { count: cart.item_count, cart: cart }
      }));
    }

    /* -- data -- */

    function refresh() {
      return fetch(routes().cart, { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.json(); })
        .then(render)
        .catch(function () { /* leave current state, the cart page is still one click away */ });
    }

    function changeLine(key, quantity, lineNode) {
      if (pending) return;
      pending = true;
      if (lineNode) lineNode.classList.add('is-busy');

      fetch(routes().cart_change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          pending = false;
          if (cart && cart.items) {
            render(cart);
          } else {
            refresh();
          }
        })
        .catch(function () {
          pending = false;
          refresh();
        });
    }

    /* -- open / close -- */

    function isOpen() {
      return !root.hidden;
    }

    function open() {
      if (isOpen()) return;
      lastFocused = document.activeElement;
      root.hidden = false;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          root.classList.add('is-open');
        });
      });
      document.documentElement.classList.add('ss-scroll-lock');
      panel.focus();
    }

    function close() {
      if (!isOpen()) return;
      root.classList.remove('is-open');
      root.hidden = true;
      document.documentElement.classList.remove('ss-scroll-lock');
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    /* -- events -- */

    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-ss-cart-close]')) {
        close();
        return;
      }
      var qtyBtn = event.target.closest('[data-qty-change]');
      if (qtyBtn) {
        var lineNode = qtyBtn.closest('.ss-cart-drawer__line');
        if (!lineNode) return;
        var quantity = parseInt(qtyBtn.dataset.qtyChange, 10);
        if (isNaN(quantity) || quantity < 0) quantity = 0;
        changeLine(lineNode.dataset.key, quantity, lineNode);
      }
    });

    root.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = panel.querySelectorAll(FOCUSABLE);
      var visible = [];
      for (var i = 0; i < focusable.length; i++) {
        if (focusable[i].offsetParent !== null) visible.push(focusable[i]);
      }
      if (!visible.length) return;
      var first = visible[0];
      var last = visible[visible.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // Global open triggers. Bound per-drawer but delegated on document.
    function onDocumentClick(event) {
      var trigger = event.target.closest('[data-ss-cart-open]');
      if (!trigger) return;
      event.preventDefault();
      refresh().then(open);
    }

    function onCartAdd() {
      refresh().then(open);
    }

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('ss:cart:add', onCartAdd);

    document.addEventListener('shopify:section:unload', function (event) {
      if (event.target.contains(root)) {
        if (isOpen()) close();
        document.removeEventListener('click', onDocumentClick);
        document.removeEventListener('ss:cart:add', onCartAdd);
      }
    });

    // Populate once so the first open is instant and the header bubble is honest.
    refresh();
  }

  function initAll() {
    var drawers = document.querySelectorAll('[data-ss-cart-drawer]');
    for (var i = 0; i < drawers.length; i++) initDrawer(drawers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();


/* Free-shipping bar hides once the threshold is met. Every tier is above it, so a
   permanently complete bar is noise. Runs after the drawer updates its own state. */
(function () {
  function sync(root) {
    var bar = root.querySelector('[data-ss-shipbar]');
    if (!bar) return;
    var threshold = parseInt(root.getAttribute('data-threshold-cents') || '0', 10);
    var total = window.__ssCartTotal;
    if (typeof total !== 'number') return;
    var thresholdMet = threshold > 0 && total >= threshold;
    bar.hidden = thresholdMet || threshold <= 0;
  }
  document.addEventListener('cartUpdated', function () {
    document.querySelectorAll('[data-ss-cart-drawer]').forEach(sync);
  });
})();
